import { createContext, ReactNode, useCallback, useContext, useState } from "react";

import { MediaFullscreenDialog, MediaFullscreenDialogVariant } from "./MediaFullscreenDialog";

interface MediaFullscreenConfig {
  title: string;
  downloadUrl?: string;
  downloadFilename?: string;
  variant?: MediaFullscreenDialogVariant;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl" | "full";
  headerIcon?: ReactNode;
  headerSubtitle?: string;
  modal?: boolean;
  content: ReactNode;
}

interface MediaFullscreenContextValue {
  openFullscreen: (config: MediaFullscreenConfig) => void;
  closeFullscreen: () => void;
  isOpen: boolean;
}

const MediaFullscreenContext = createContext<MediaFullscreenContextValue | null>(null);

export const useMediaFullscreen = () => {
  const context = useContext(MediaFullscreenContext);
  if (!context) {
    throw new Error("useMediaFullscreen must be used within a MediaFullscreenProvider");
  }
  return context;
};

interface MediaFullscreenProviderProps {
  children: ReactNode;
}

export const MediaFullscreenProvider = ({ children }: MediaFullscreenProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<MediaFullscreenConfig | null>(null);

  const openFullscreen = useCallback((newConfig: MediaFullscreenConfig) => {
    setConfig(newConfig);
    setIsOpen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <MediaFullscreenContext.Provider value={{ openFullscreen, closeFullscreen, isOpen }}>
      {children}
      <MediaFullscreenDialog
        isOpen={isOpen}
        onClose={closeFullscreen}
        title={config?.title ?? ""}
        downloadUrl={config?.downloadUrl}
        downloadFilename={config?.downloadFilename}
        variant={config?.variant}
        maxWidth={config?.maxWidth}
        headerIcon={config?.headerIcon}
        headerSubtitle={config?.headerSubtitle}
        modal={config?.modal}
      >
        {config?.content}
      </MediaFullscreenDialog>
    </MediaFullscreenContext.Provider>
  );
};
